import { duration } from 'moment';
import { MeasurementType } from '../../api/studykit';
import { GraphSettings, XAxisSettings, YAxisSettings } from './graph-single';

const DEFAULT_TIME_AXIS: XAxisSettings = {
  dataKey: 'time',
  name: 'time',
};

const DEFAULT_QUALITY_AXIS: YAxisSettings = {
  id: 'y',
  name: 'quality',
  dataKeys: ['quality'],
  graphType: 'line',
  axisType: 'value',
  domainStart: 1,
  domainEnd: 4,
};

interface OverrideableSettings {
  xAxis?: Partial<XAxisSettings>;
  groupBy?: string;
  yAxes?: Record<string, Partial<YAxisSettings> & { dataKeys?: string[] }>;
  hideYAxes?: string[];
  description?: string;
}

const GRAPH_SETTINGS: Record<string, OverrideableSettings> = {
  Electrocardiogram: {
    groupBy: 'channelType',
    yAxes: {
      ecg: {
        hideQualityMarkers: true,
        hideMarkersAtThreshold: duration(2000, 'milliseconds'),
        mapValue: (digitalValue, index, data) => {
          if (digitalValue === null) return null;
          const digitalMax = 4095;
          const physicalMax = 5644;
          const digitalPercentage = digitalValue / digitalMax;
          const physical = physicalMax * 2 * digitalPercentage - physicalMax;
          return physical;
        },
        unit: 'µV',
      },
    },
  },
  Acceleration: {
    yAxes: {
      acceleration: {
        hideQualityMarkers: true,
        hideMarkersAtThreshold: duration(5, 'seconds'),
        dataKeys: ['x', 'y', 'z'],
        mapValue: (value, index, data) => {
          if (value === null) return null;
          const sensitivity = 512; // lbs/g
          return value / sensitivity;
        },
        unit: 'g',
      },
    },
  },
  Photoplethysmogram: {
    hideYAxes: [],
    yAxes: {
      ppg: {
        hideQualityMarkers: true,
        hideMarkersAtThreshold: duration(5, 'seconds'),
        unit: 'green',
      },
      ambient: {
        hideQualityMarkers: true,
        hideMarkersAtThreshold: duration(5, 'seconds'),
      },
      ledPower: {
        hideQualityMarkers: true,
        hideMarkersAtThreshold: duration(5, 'seconds'),
        unit: '%',
        defaultDisabled: true,
      },
      adcGain: {
        hideQualityMarkers: true,
        hideMarkersAtThreshold: duration(5, 'seconds'),
        defaultDisabled: true,
      },
    },
  },
  CardioFitnessIndex: {
    yAxes: {
      cfIndex: {
        unit: '%',
      },
    },
  },
  RestingHeartRate: {
    yAxes: {
      restingHeartRate: {
        unit: 'bpm',
      },
    },
  },
  AverageHeartRate: {
    yAxes: {
      heartRate: {
        unit: 'bpm',
      },
    },
  },
  IntermittentHeartRate: {
    yAxes: {
      heartRate: {
        unit: 'bpm',
      },
    },
  },
  HeartRhythmType: {
    hideYAxes: ['delay'],
    yAxes: {
      type: {
        mappings: {
          0: 'Other',
          1: 'Atrial\nfibrillation',
          255: 'Unspecified',
        },
      },
    },
  },
  HeartBeats: {
    hideYAxes: ['env', 'type'],
    yAxes: {
      interval: {
        name: 'Interbeat interval',
        unit: 'sec',
        hideMarkersAtThreshold: duration({ minutes: 20 }),
        hideQualityMarkers: true,
      },
    },
  },
  RespiratoryRate: {
    yAxes: {
      respiratoryRate: {
        mapValue: (v) => (v === null ? null : v / 4),
        unit: 'breaths/min',
      },
    },
  },
  SkinProximity: {
    yAxes: {
      skinProximity: {
        mappings: {
          255: 'Unspecified',
          254: 'Off skin',
          0: 'On skin',
        },
      },
    },
  },
  ActivityType: {
    yAxes: {
      activityType: {
        mappings: {
          0: 'Unspecified',
          1: 'Other',
          2: 'Walk',
          4: 'Run',
          6: 'Cycle',
          7: 'Rest',
        },
      },
    },
  },
  ActivityCount: {
    yAxes: {
      counts: {
        graphType: 'bar',
        hideQualityMarkers: true,
      },
    },
  },
  Speed: {
    yAxes: {
      speed: {
        graphType: 'bar',
        unit: 'm/s',
        mapValue: (v) => (v === null ? null : v / 10),
      },
    },
  },
  Weight: {
    yAxes: {
      weight: {
        unit: 'kg',
        mapValue: (v) => (v === null ? null : v / 10),
      },
    },
  },
  Height: {
    yAxes: {
      height: {
        unit: 'cm',
      },
    },
  },
  Cadence: {
    yAxes: {
      cadence: {
        unit: 'min⁻¹',
        graphType: 'bar',
      },
    },
  },
  VO2Max: {
    yAxes: {
      vo2Max: {
        unit: 'ml/kg/min',
      },
    },
  },
  ActivityEnergyExpenditure: {
    yAxes: {
      energyExpenditure: {
        unit: 'kcal/h',
        // type: 'bar',
      },
    },
  },
  IntermittentEnergyExpenditure: {
    yAxes: {
      energyExpenditure: {
        unit: 'kcal/h',
      },
    },
  },
  SleepSession: {
    yAxes: {
      sleepStage: {
        mappings: {
          0: 'unclassified',
          1: 'deep sleep',
          2: 'light sleep',
          3: 'REM sleep',
          4: 'wake',
        },
      },
    },
  },
  HeartRateZone: {
    yAxes: {
      heartRateZone: {
        hideQualityMarkers: true,
        mappings: {
          1: 'Zone 1',
          2: 'Zone 2',
          3: 'Zone 3',
          4: 'Zone 4',
        },
      },
    },
  },
  ArterialBloodPressure: {
    hideYAxes: ['date'],
  },
  SleepQuality: {
    hideYAxes: ['date'],
    yAxes: {
      duration: {
        hideQualityMarkers: true,
      },
      sleepQuality: {
        hideQualityMarkers: true,
      },
    },
  },
  HandActivityType: {
    yAxes: {
      handActivityType: {
        mappings: {
          0: 'Unspecified',
          1: 'Other',
          2: 'Scratching',
        },
      },
    },
  },
};

export function hasCategoricalData(measurementType: string): boolean {
  const settings = GRAPH_SETTINGS[measurementType];
  return Object.values(settings?.yAxes || {}).some((y) => y !== DEFAULT_QUALITY_AXIS && y.mappings);
}

export function createGraphSettings(
  measurementType: MeasurementType,
  qualityKey: string = 'quality',
): GraphSettings {
  const settings = GRAPH_SETTINGS[measurementType.name];

  const customYAxesKeys: [string, string[]][] = Object.entries(settings?.yAxes || {}).map(
    ([k, v]) => [k, v.dataKeys || [k]],
  );

  const fields = measurementType.fields.slice();
  if (measurementType.name === 'HeartBeats') {
    fields.push('interval');
  }

  const generatedYAxesKeys: [string, string[]][] = fields
    .filter((k) => k !== DEFAULT_TIME_AXIS.dataKey && k !== qualityKey)
    .filter((k) => !customYAxesKeys.flatMap(([_, keys]) => keys).includes(k))
    .filter((k) => !settings?.hideYAxes?.includes(k))
    .map((k) => [k, [k]]);

  const graphSettings: GraphSettings = {
    groupBy: settings?.groupBy,
    qualityKey,
    xAxis: DEFAULT_TIME_AXIS,
    yAxes: [...customYAxesKeys, ...generatedYAxesKeys].map(([name, dataKeys], idx) => ({
      id: idx === 0 ? 'y' : 'y' + (idx + 1),
      name,
      graphType: 'line',
      axisType: 'value',
      dataKeys,
    })),
  };

  return {
    ...graphSettings,
    yAxes: graphSettings.yAxes
      ?.filter((yAxis) => !settings?.hideYAxes?.includes(yAxis.name))
      ?.map((yAxis) => {
        const axisSettings = settings?.yAxes?.[yAxis.name];
        if (!axisSettings) return yAxis;

        const newYAxis: YAxisSettings = {
          ...yAxis,
          ...axisSettings,
        };

        const mappings = axisSettings.mappings;
        if (!mappings) return newYAxis;

        return {
          ...newYAxis,
          axisType: 'category',
          mapValue: (v) => mappings[v],
          mappings,
        } as YAxisSettings;
      }),
  };
}
